import { useReactToPrint } from "react-to-print";
import { Button, Form, Input, Modal, Pagination, Table, message } from "antd";
import type { PaginationProps } from "antd";
import {
  EditOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import "./styles.css";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AggregateView,
  FiltersUsed,
  PaymentMethodView,
  RecordView,
  TableView,
} from "../../components/_shared/DetailsPageView";
import {
  resetInvoices,
  retrieveInvoiceDetails,
  sendInvoice,
  updateInvoice,
} from "../../redux/actions/invoiceActions";
import { RootState } from "../../redux/rootReducer";
import {
  AccountType,
  Currency,
  InvoiceDefaultNotes,
  InvoiceStatus,
} from "../../shared/constant";
import dateUtils from "../../utils/dateUtils";
import { getInvoiceDues } from "../../redux/actions/duesActions";
import CreatePaymentModal from "../../components/PaymentDetails/CreatePaymentModal";
import tableUtils from "../../utils/tableUtils";
import {
  getClientAdjustmentAmount,
  getNoPageClientAdjAmount,
} from "../../redux/actions/accountActions";
import { ClientAdjustmentColumn } from "../../shared/table/columns";
import InvoiceToPrint from "../../components/InvoiceDetails/InvoiceToPrint";
import ROUTES from "../../routes";
import clapUtils, {
  isAccountsORManager,
  isClapAdminORClapManager,
} from "../../utils/clapUtils";
import SpinnerView from "../../components/_shared/SpinnerView";

const InvoiceDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const [effect, setEffect] = useState(false);
  const [visible, setVisible] = useState(false);
  const [noteRefModal, setNoteRefModal] = useState(false);
  const [modalType, setModalType] = useState("");
  // const [invoiceSentStatus, setInvoiceSentStatus] = useState(false);
  const [page, setPage] = useState(1);
  const {
    invoiceDetails,
    sendInvoicePending,
    sendInvoiceFulfilled,
    updateInvoiceSuccess,
  } = useSelector((state: RootState) => state.invoice);
  const { invoiceDues } = useSelector((state: RootState) => state.dues);
  const { userDetails } = useSelector((state: RootState) => state.user);
  const {
    clientAdjustments,
    clientAdjustmentsResponse,
    noPageclientAdjustments,
    clientAdjustmentsPending,
  } = useSelector((state: RootState) => state.account);
  const [form] = Form.useForm();

  useEffect(() => {
    if (invoiceDetails)
      form.setFieldsValue({
        notes: invoiceDetails.notes || InvoiceDefaultNotes,
        reference: invoiceDetails.reference,
      });
  }, [invoiceDetails]);

  // TODO Merge: invoice details and payment details

  const [invoiceCurrency, setInvoiceCurrency] = useState("");

  const [headerData, setHeaderData] = useState<any>(null);

  const componentRef = useRef(null);

  const handleAfterPrint = React.useCallback(() => {
    // console.log("`onAfterPrint` called");
  }, []);

  const handleBeforePrint = React.useCallback(() => {
    // console.log("`onBeforePrint` called");
  }, []);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    // onBeforeGetContent: handleOnBeforeGetContent,
    onBeforePrint: handleBeforePrint,
    onAfterPrint: handleAfterPrint,
    removeAfterPrint: true,
  });

  useEffect(() => {
    dispatch(resetInvoices());
    dispatch(retrieveInvoiceDetails({ id: invoiceId }));
  }, [effect]);

  useEffect(() => {
    // console.log("invoiceDetails", invoiceDetails);
    if (invoiceDetails) {
      dispatch(
        getInvoiceDues({
          id: invoiceDetails.id,
        }),
      );
      dispatch(
        getNoPageClientAdjAmount({
          invoiceId: invoiceDetails.id,
        }),
      );

      if (isAccountsORManager(userDetails)) {
        if (
          invoiceDetails.client_details.client_types[0] === AccountType.LABEL
        ) {
          setHeaderData({
            c1: "Reporting Period",
            c3: `Gross (${invoiceDetails.display_currency})`,
            // c4: `Payable (${invoiceDetails.display_currency})`,
          });
        }
        if (
          invoiceDetails.client_details.client_types[0] === AccountType.MEMBER
        ) {
          setHeaderData({
            c1: "Reporting Period",
            // c3: `Gross (${invoiceDetails.display_currency})`,
            c4: `Payable (${invoiceDetails.display_currency})`,
          });
        }
      } else {
        setHeaderData({
          c1: "Reporting Period",
          c3: `Gross (${invoiceDetails.display_currency})`,
          c4: `Payable (${invoiceDetails.display_currency})`,
        });
      }

      setInvoiceCurrency(
        clapUtils.getCurrencyIcon(invoiceDetails.display_currency),
      );
    }
  }, [invoiceDetails]);

  useEffect(() => {
    if (invoiceDetails) {
      dispatch(
        getClientAdjustmentAmount({
          page,
          invoiceId: invoiceDetails.id,
        }),
      );
    }
  }, [invoiceDetails, page]);

  const handlePageChange: PaginationProps["onChange"] = (p) => {
    setPage(p);
  };

  const buildTableData = () => {
    const results = invoiceDues?.period_wise_amount?.map((dues: any) => {
      const period = Object.keys(dues)[0];
      if (isAccountsORManager(userDetails)) {
        if (
          invoiceDetails.client_details.client_types[0] === AccountType.LABEL
        ) {
          return {
            c1: period,
            c3: `${invoiceCurrency} ${dues[period].gross_amount}`,
            // c4: `${invoiceCurrency} ${dues[period].payable_amount}`,
          };
        }
        if (
          invoiceDetails.client_details.client_types[0] === AccountType.MEMBER
        ) {
          return {
            c1: period,
            // c3: `${invoiceCurrency} ${dues[period].gross_amount}`,
            c4: `${invoiceCurrency} ${dues[period].payable_amount}`,
          };
        }
      }
      return {
        c1: period,
        c3: `${invoiceCurrency} ${dues[period].gross_amount}`,
        c4: `${invoiceCurrency} ${dues[period].payable_amount}`,
      };
    });
    return results;
  };

  const cData = {
    invoiceDetails,
    invoiceDues,
    invoiceCurrency,
    headerData,
    noPageclientAdjustments,
  };

  const handleSendInvoice = () => {
    Modal.confirm({
      title: (
        <div>
          {invoiceDetails?.client_details?.is_email_notification_active ? (
            "Are you sure to send invoice?"
          ) : (
            <div>
              <div>Are you sure you have sent offline invoice?</div>
              <div className="text-[13px]">(Email will not be sent)</div>
            </div>
          )}
        </div>
      ),
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      cancelText: "No",
      // centered: true,
      onOk: () => {
        if (invoiceDetails?.client_details?.is_email_notification_active) {
          dispatch(
            sendInvoice({
              id: invoiceDetails?.id,
              body: {
                exchange_rate_added_at: dateUtils.defaultFormat(
                  invoiceDetails.exchange_rate_added_at,
                ),
                payment_date: invoiceDetails.paid_date || "",
                client_dues: { ...invoiceDues },
              },
            }),
          );
        } else {
          dispatch(
            updateInvoice({
              id: invoiceDetails?.id,
              status: InvoiceStatus.INVOICE_SENT,
            }),
          );
        }
      },
      onCancel() {},
    });
  };

  useEffect(() => {
    if (sendInvoiceFulfilled) {
      message.success("Invoice sent in mail successfully");
      setEffect(!effect);
      // setInvoiceSentStatus(true);
    }
  }, [sendInvoicePending]);

  // useEffect(() => {
  //   console.log(clientDues);
  // }, [clientDues]);

  const showModal = () => {
    setVisible(true);
  };

  const hideModal = () => {
    setVisible(false);
  };

  useEffect(() => {
    if (updateInvoiceSuccess) setEffect(!effect);
  }, [updateInvoiceSuccess]);

  const onNoteRefEdit = (type: string) => {
    form.setFieldsValue({
      ...(type === "NOTES" && {
        notes: invoiceDetails.notes || InvoiceDefaultNotes,
      }),
      ...(type === "REFERENCE" && {
        reference: invoiceDetails.reference,
      }),
    });
    setModalType(type);
    setNoteRefModal(true);
  };

  const onNoteRefCancel = () => {
    setModalType("");
    setNoteRefModal(false);
    form.resetFields();
  };

  const updateNoteRef = async () => {
    try {
      await form.validateFields();
    } catch (errorInfo) {
      return;
    }

    console.log(modalType);

    const formFields = form.getFieldsValue(true);
    dispatch(
      updateInvoice({
        id: invoiceDetails.id,
        ...(modalType === "NOTES" && {
          notes: formFields.notes || InvoiceDefaultNotes,
        }),
        ...(modalType === "REFERENCE" && {
          reference: formFields.reference || "DEFAULT",
        }),
      }),
    );
    onNoteRefCancel();
  };

  const onPaymentTitleClick = () => {
    navigate(`${ROUTES.PATHS.ADMIN.PAYMENTS}/${invoiceDetails.payment_id}`);
  };

  return (
    <>
      <SpinnerView isFetching={!(invoiceDetails && invoiceDues)}>
        {invoiceDetails && invoiceDues && (
          <div>
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                <div className="text-heading20">
                  {invoiceDetails?.invoice.invoice_no}
                </div>
                <div>{clapUtils.getInvoiceType(invoiceDetails.status)}</div>
                <Button className="ml-2.5" onClick={handlePrint}>
                  Print Invoice
                </Button>
                {invoiceDetails.paid_date && (
                  <Button
                    type="primary"
                    loading={sendInvoicePending as boolean}
                    onClick={handleSendInvoice}
                    disabled={
                      !invoiceDetails?.client_details
                        ?.is_email_notification_active &&
                      ![InvoiceStatus.DRAFT, InvoiceStatus.VOID].includes(
                        invoiceDetails.status,
                      )
                    }
                  >
                    Send Invoice
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                {isClapAdminORClapManager(userDetails) &&
                  !invoiceDetails.paid_date &&
                  invoiceDetails.status !== InvoiceStatus.VOID && (
                    <Button
                      type={
                        invoiceDetails.status !== InvoiceStatus.APPROVED
                          ? "primary"
                          : "default"
                      }
                      loading={sendInvoicePending as boolean}
                      onClick={handleSendInvoice}
                      disabled={
                        !invoiceDetails?.client_details
                          ?.is_email_notification_active &&
                        ![InvoiceStatus.DRAFT].includes(invoiceDetails.status)
                      }
                    >
                      Send Invoice
                    </Button>
                  )}
                {isClapAdminORClapManager(userDetails) &&
                  !invoiceDetails.paid_date &&
                  invoiceDetails.status !== InvoiceStatus.VOID && (
                    <div>
                      <Button
                        type="primary"
                        onClick={showModal}
                        disabled={
                          invoiceDetails.paid_date ||
                          invoiceDetails.status !== InvoiceStatus.APPROVED
                        }
                      >
                        Create Payment
                      </Button>
                      {visible && (
                        <CreatePaymentModal
                          visible={visible}
                          onCancel={hideModal}
                        />
                      )}
                    </div>
                  )}
                {isClapAdminORClapManager(userDetails) &&
                  invoiceDetails.paid_date && (
                    <div
                      onClick={onPaymentTitleClick}
                      className="text-lg text-dodger-blue underline cursor-pointer"
                      aria-hidden="true"
                    >
                      {invoiceDetails.payment_title}
                    </div>
                  )}
                {/* <Button
                icon={icons.DotsHorizontal()}
                className="flex items-center justify-center"
              /> */}
              </div>
              <div style={{ display: "none" }}>
                <InvoiceToPrint {...cData} ref={componentRef} />
              </div>
            </div>
            <div className="mt-6 flex justify-between items-center w-full">
              <div className="flex gap-12">
                <div className="max-w-lg">
                  <RecordView
                    title="Name:"
                    text={invoiceDetails?.client_details.name}
                  />
                  <RecordView
                    title="Phone:"
                    text={invoiceDetails?.client_details.phone}
                  />
                </div>
                <div>
                  <RecordView
                    title="Invoice Date:"
                    text={dateUtils.defaultFormat(invoiceDetails?.created_at)}
                  />
                  <RecordView
                    title="Address:"
                    text={invoiceDetails?.client_details.address}
                  />
                </div>
              </div>
              <div>
                <div className="text-secondary mb-1.5">
                  Invoice Amount ({invoiceDetails.display_currency})
                </div>
                <div className="text-heading20 text-right">
                  {invoiceCurrency} {invoiceDetails.invoice.invoice_amount}
                </div>
              </div>
            </div>
            <div className="mb-9 flex gap-3">
              <div className="text-secondary">Ref:</div>
              <div className="flex gap-x-3">
                {invoiceDetails.reference}{" "}
                {isClapAdminORClapManager(userDetails) && (
                  <div onClick={() => onNoteRefEdit("REFERENCE")} aria-hidden>
                    <Button size="small" type="text" icon={<EditOutlined />} />
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="font-semibold mb-2">Filters Used</div>
              <div className="default-border p-4 space-y-2">
                <FiltersUsed details={invoiceDetails} />
              </div>
              {!!invoiceDues.period_wise_amount.length && (
                <div className="mt-5">
                  <div className="bg-platinum">
                    <TableView {...headerData} twClass="pt-3" header />
                    <div className="flash-border my-[13px]" />
                  </div>
                  {buildTableData().map((data: any) => (
                    <div key={data.c1}>
                      <TableView {...data} />
                      <div className="flash-border my-[13px]" />
                    </div>
                  ))}
                  {isClapAdminORClapManager(userDetails) && (
                    <TableView
                      {...{
                        c1: "Total",
                        c3: `${invoiceCurrency} ${invoiceDues.total_gross_income}`,
                        c4: `${invoiceCurrency} ${invoiceDues.total_payable}`,
                      }}
                      twClass="mt-[13px]"
                      header
                    />
                  )}
                  {isAccountsORManager(userDetails) && (
                    <>
                      {invoiceDetails.client_details.client_types[0] ===
                        AccountType.LABEL && (
                        <TableView
                          {...{
                            c1: "Total",
                            c3: `${invoiceCurrency} ${invoiceDues.total_gross_income}`,
                            // c4: `${invoiceCurrency} ${invoiceDues.total_payable}`,
                          }}
                          twClass="mt-[13px]"
                          header
                        />
                      )}
                      {invoiceDetails.client_details.client_types[0] ===
                        AccountType.MEMBER && (
                        <TableView
                          {...{
                            c1: "Total",
                            // c3: `${invoiceCurrency} ${invoiceDues.total_gross_income}`,
                            c4: `${invoiceCurrency} ${invoiceDues.total_payable}`,
                          }}
                          twClass="mt-[13px]"
                          header
                        />
                      )}
                    </>
                  )}
                  {invoiceDues.exchange_rate && (
                    <>
                      <TableView
                        {...{
                          c1: `Exchange rate on:  ${dateUtils.defaultFormat(
                            invoiceDetails.exchange_rate_added_at,
                          )}`,
                          c3: "Exchange Rate",
                          c4: invoiceDues.exchange_rate,
                        }}
                        twClass="mt-[13px]"
                      />
                      <AggregateView
                        title="Amount in BDT"
                        value={`${clapUtils.getCurrencyIcon(Currency.BDT)} ${
                          invoiceDues.amount_in_bdt
                        }`}
                      />
                    </>
                  )}
                </div>
              )}
              {/* <AggregateView title="TAX" value="- 0.0" />
            <AggregateView title="Total Payment" value="€ 87.00" /> */}
            </div>
            {invoiceDetails.paid_date && (
              <div className="mt-12">
                <div className="flex gap-3">
                  <div className="text-secondary">Payment Date: </div>
                  <span>{invoiceDetails.paid_date}</span>
                </div>
                <PaymentMethodView details={invoiceDetails} />
              </div>
            )}
            {/* <div className="py-10 h-[1000px]">
            <InvoiceToPrint {...cData} ref={componentRef} />
          </div> */}
            {!!clientAdjustments.length && (
              <div>
                <div className="font-semibold text-[15px] mt-10 mb-1">
                  Advance Payment
                </div>
                <div>
                  <Table
                    rowKey={(record: any) => {
                      return record.id;
                    }}
                    columns={tableUtils.buildColumns(
                      clientAdjustments,
                      ClientAdjustmentColumn,
                    )}
                    dataSource={clientAdjustments}
                    pagination={false}
                    size="middle"
                    loading={{
                      spinning: clientAdjustmentsPending as boolean,
                      indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                    }}
                  />
                </div>
                <div className="text-right mt-4">
                  <Pagination
                    current={page}
                    onChange={handlePageChange}
                    total={clientAdjustmentsResponse?.count}
                    showSizeChanger={false}
                  />
                </div>
              </div>
            )}

            <div className="mt-9 pb-4">
              <div
                className="flex items-center cursor-pointer w-16"
                onClick={() => onNoteRefEdit("NOTES")}
                aria-hidden="true"
              >
                <div className="font-semibold">Notes</div>
                {isClapAdminORClapManager(userDetails) && (
                  <div>
                    <Button size="small" type="text" icon={<EditOutlined />} />
                  </div>
                )}
              </div>
              <div className="w-4/12">
                {invoiceDetails.notes || InvoiceDefaultNotes}
              </div>
            </div>
          </div>
        )}
      </SpinnerView>
      <Modal
        title={`Update ${modalType === "NOTES" ? "Notes" : "Reference"} `}
        centered
        visible={noteRefModal}
        onOk={updateNoteRef}
        onCancel={onNoteRefCancel}
        className="notesModalStyle"
      >
        <div className="mx-6 mt-2 mb-8">
          <Form form={form} layout="vertical" name="notesForm">
            <Form.Item
              name={modalType === "NOTES" ? "notes" : "reference"}
              label={` ${modalType === "NOTES" ? "Notes" : "Ref."} Description`}
              initialValue=""
            >
              <Input.TextArea
                showCount
                maxLength={modalType === "NOTES" ? 200 : 100}
                rows={modalType === "NOTES" ? 4 : 2}
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default InvoiceDetails;
